/**
 * The chat, derived. `buildTranscript(session, snapshot, data)` is pure: the same browser session and the
 * same BFF snapshot always give the same lines, so a reload, a second tab or a BFF restart rebuilds the
 * same conversation. Nothing here is timing: first-render pacing of the lines is the view's business
 * (useEntranceStages) and never feeds back into state.
 *
 * The flow (template-project's onboarding-v2 order, on our real data; plan phase 3, section J):
 *   opener -> site -> research card -> quiz (docked, one question at a time, latest ack only)
 *   -> Juno "that's everything" -> Scout ads card (ad accounts confirm docked while the job waits on it; skipped without Meta)
 *   -> Scout funnel card (only when the BFF started a funnel job; it never docks, so the flow goes on)
 *   -> Juno brand guide card -> brand profile confirm (docked, kept in the browser session)
 *   -> Scout competitors research card, competitors confirm docked -> Scout cohort line
 *   -> Juno hand-off with the inline "Build my assessment" card -> restart dock
 * No line ever waits on a timer, and there are no "thinking" items: a step that is still working shows its state in its card.
 */
import {
  AD_PLATFORM_SUPPORTED,
  AD_PLATFORMS,
  adPlatformOfChannel,
  adPlatformOfJobKind,
  type AdAccount,
  type AdPlatform,
  type BrandGuideResponse,
  type Competitor,
  type GetAdAccountsResponse,
  type GetCompetitorsResponse,
  type NotAStoreDetails,
  type SessionSnapshot,
  type StepState,
} from '@/contracts/onboarding-session';

import { COMPOUND_SECOND_LEVELS, COMPETITOR_PICKS_MAX, COUNTRY_CODE_LENGTH } from '../constants';
import {
  ADS_ACCOUNTS_SUBTITLE,
  adAccountsTitle,
  adsNone,
  cohortReadyLine,
  competitorsSubtitle,
  COPY,
  FALLBACK_BRAND_LABEL,
  metaAdsLine,
  presenceReply,
} from '../copy';
import { detectPresence } from '../quiz/presence';
import {
  channelName,
  paidChannels,
  planFor,
  questionTitle,
  replyTo,
  visibleOptions,
  type Answer,
  type Answers,
  type Question,
} from '../quiz/questions';
import { normalizeDomain } from '../quiz/website';
import { adsStepPosition } from './adsStepPosition';
import { isTerminalState } from './jobState';
import { researchProductCount } from './researchSteps';
import type { OnboardingSessionState } from './session';

/** A loadable piece of data the transcript depends on (the query hooks reduce to this). */
export type DataState<T> =
  | { state: 'idle' }
  | { state: 'loading' }
  | { state: 'error' }
  | { state: 'ready'; data: T };

export interface TranscriptData {
  adAccounts?: DataState<GetAdAccountsResponse>;
  competitors?: DataState<GetCompetitorsResponse>;
  /** A social or marketplace address the person gave and the chat refused (in memory; not persisted). */
  /** The name the brand guide read (the brand's label when the research has not named it). */
  brandGuideName?: string | null;
  rejectedUrl?: { raw: string; reason: 'not_a_store' | 'invalid_url' | 'rate_limited'; presence?: NotAStoreDetails } | null;
}

type Base = { id: string };

/** A card drawn under an agent's lines, in the same message (radar: `juno(text, card)`). */
export type CardSpec =
  | { kind: 'research' }
  | { kind: 'brandGuide' }
  | { kind: 'ads'; platform: AdPlatform }
  | { kind: 'funnel' }
  | { kind: 'competitorsResearch'; brand: string }
  | {
      /** The inline "Build my assessment" card (TP's assessment artifact button): offered until the click. */
      kind: 'build';
      brand: string;
      /** The BFF holds the quiz answers (`snapshot.quizSubmitted`); until then Build must not be offered as done. */
      ready: boolean;
    };

export type TranscriptItem =
  | (Base & { kind: 'user'; text: string })
  | (Base & { kind: 'agent'; agent: 'juno' | 'scout'; lines: string[]; card?: CardSpec });

export type DockSpec =
  | { kind: 'url' }
  | {
      kind: 'question';
      question: Question;
      title: string;
      index: number;
      total: number;
      /** Set when an answered question is shown again (Back): its answer starts filled in. */
      initial?: Answer;
    }
  | {
      kind: 'adAccounts';
      platform: AdPlatform;
      platformLabel: string;
      title: string;
      subtitle: string;
      accounts: AdAccount[];
      /** The discovery is still running: more pages may show up. */
      searching: boolean;
    }
  | {
      kind: 'competitors';
      title: string;
      subtitle: string;
      competitors: Competitor[];
      max: number;
    }
  | {
      /** The brand profile to confirm before the competitors are looked for (kept in the browser session). */
      kind: 'brandProfile';
      brand: string;
    };

export type TranscriptPhase = 'url' | 'quiz' | 'ads' | 'brandProfile' | 'competitors' | 'build' | 'complete';

export interface Transcript {
  items: TranscriptItem[];
  /** What the bottom of the chat asks for now; null while the chat is waiting on research. */
  dock: DockSpec | null;
  phase: TranscriptPhase;
  /** True once every quiz question in the plan is answered or skipped. */
  quizDone: boolean;
}

const platformLabel = (platform: AdPlatform) => channelName(platform);

/** The answered questions of the active plan, in plan order. */
export function answeredQuestions(answers: Answers): Question[] {
  return planFor(answers).filter((q) => answers[q.id] !== undefined);
}

/** The first question of the plan that has no answer yet, or null when the quiz is done. */
export function currentQuestion(answers: Answers): Question | null {
  return planFor(answers).find((q) => answers[q.id] === undefined) ?? null;
}

/**
 * The dock for an answered question the person went Back to: the same card as when it was first asked, with
 * its answer to start from. Null when `reviewId` is not an answered question of the plan (the caller then
 * shows the first unanswered one). Pure, like everything here: the review itself is UI state, not stored.
 */
export function reviewDock(answers: Answers, reviewId: string | null): Extract<DockSpec, { kind: 'question' }> | null {
  if (reviewId === null) return null;
  const plan = planFor(answers);
  const at = plan.findIndex((q) => q.id === reviewId);
  const question = plan[at];
  const initial = question ? answers[question.id] : undefined;
  if (!question || !initial) return null;
  return {
    kind: 'question',
    question: visibleOptions(question, answers),
    title: questionTitle(question, answers),
    index: at + 1,
    total: plan.length,
    initial,
  };
}

/**
 * The ad platform a quiz channel stands for ("meta", or an alias such as "Facebook"), or null. The contract
 * helper looks aliases up on a plain object, so its answer is checked against the platform list here: a
 * typed "constructor" must never come back as a platform.
 */
function platformOfChannel(channel: string): AdPlatform | null {
  const platform: unknown = adPlatformOfChannel(channel);
  return typeof platform === 'string' && (AD_PLATFORMS as readonly string[]).includes(platform)
    ? (platform as AdPlatform)
    : null;
}

/** The ad platforms the client named that have a discovery connector, once each, in the order they named them. */
export function namedSupportedPlatforms(answers: Answers): AdPlatform[] {
  const platforms: AdPlatform[] = [];
  for (const channel of paidChannels(answers)) {
    const platform = platformOfChannel(channel);
    if (platform && Object.hasOwn(AD_PLATFORM_SUPPORTED, platform) && AD_PLATFORM_SUPPORTED[platform] && !platforms.includes(platform)) {
      platforms.push(platform);
    }
  }
  return platforms;
}

function agentLine(id: string, lines: string[], card?: CardSpec, agent: 'juno' | 'scout' = 'juno'): TranscriptItem {
  return { id, kind: 'agent', agent, lines, ...(card ? { card } : {}) };
}

/** `shop.truekind.co.uk` -> "Truekind": the registrable name of a host, capitalised. The last resort for a brand's name (see `brandLabel`). */
function nameFromDomain(host: string): string {
  const labels = host.replace(/^www\./, '').split('.').filter(Boolean);
  const tail = labels.at(-1) ?? '';
  const second = labels.at(-2) ?? '';
  // `co.uk`, `com.au`: a two-letter country code behind a generic second level.
  const compound = labels.length >= 3 && tail.length === COUNTRY_CODE_LENGTH && COMPOUND_SECOND_LEVELS.includes(second);
  const name = (compound ? labels.at(-3) : labels.length >= 2 ? second : tail) ?? '';
  return name.charAt(0).toUpperCase() + name.slice(1);
}

/**
 * What the chat calls the brand: the name the research read, else the one in the brand guide, and only as a last
 * resort one made from the site's own address. Never invented: with neither a name nor a site it falls back to
 * "your brand".
 */
export function brandLabel(snapshot: SessionSnapshot | null, url: string | null, guideName?: string | null): string {
  const researched = snapshot?.research.name?.trim();
  if (researched) return researched;
  const guided = guideName?.trim();
  if (guided) return guided;
  const host = snapshot?.domain || (url ? normalizeDomain(url) : '');
  return nameFromDomain(host) || FALLBACK_BRAND_LABEL;
}

const userBubble = (id: string, text: string): TranscriptItem => ({ id, kind: 'user', text });

function accountNames(platform: AdPlatform, ids: string[], accounts: AdAccount[]): string {
  const byId = new Map(accounts.map((a) => [a.accountId, a.name ?? a.accountId]));
  return ids.map((id) => byId.get(id) ?? id).join(', ') || platformLabel(platform);
}

/* ------------------------------ the sections ------------------------------ */

/**
 * What every section reads and appends to. A section pushes its lines onto `items` and either returns the transcript
 * where the flow stops (a dock, or waiting on data) or `null` to let the next section go on.
 */
interface Flow {
  session: OnboardingSessionState;
  snapshot: SessionSnapshot | null;
  data: TranscriptData;
  items: TranscriptItem[];
}

const stopAt = (
  { items }: Flow,
  phase: TranscriptPhase,
  dock: DockSpec | null,
  quizDone: boolean,
): Transcript => ({ items, dock, phase, quizDone });

/** 1. The website. Until it is given, the chat is the opener, Juno's question and (if the last try was refused) why. */
function urlSection(flow: Flow): Transcript | null {
  const { session, data, items } = flow;
  if (session.url !== null) return null;
  const rejected = data.rejectedUrl;
  if (rejected) {
    items.push(userBubble('rejected-url', rejected.raw));
    const presence = rejected.presence ?? detectPresence(rejected.raw);
    const reply =
      rejected.reason === 'rate_limited'
        ? [...COPY.rateLimited]
        : presence && rejected.reason === 'not_a_store'
          ? presenceReply(presence)
          : [...COPY.invalidUrl];
    items.push(agentLine('rejected-reply', reply));
  }
  return stopAt(flow, 'url', { kind: 'url' }, false);
}

/** 2. The website is in: the research card, then the quiz, one docked question at a time with only the latest ack. */
function quizSection(flow: Flow): Transcript | null {
  const { session, items } = flow;
  items.push(userBubble('site', session.url!));
  items.push(agentLine('research-intro', [...COPY.researchIntro], { kind: 'research' }));

  const answers = session.answers;
  const plan = planFor(answers);
  const answered = answeredQuestions(answers);
  const current = currentQuestion(answers);

  if (answered.length > 0) {
    // Only the latest acknowledgement, replaced on each answer and never stacked. A skip says nothing.
    const last = answered[answered.length - 1]!;
    const ack = replyTo(last, answers[last.id]!, answers);
    if (ack) items.push(agentLine('ack', [ack]));
  }
  if (!current) return null;
  return stopAt(
    flow,
    'quiz',
    {
      kind: 'question',
      question: visibleOptions(current, answers),
      title: questionTitle(current, answers),
      index: plan.indexOf(current) + 1,
      total: plan.length,
    },
    false,
  );
}

/** 3-4. J1 "that's everything", then J2: the ads, one platform at a time (skipped without a supported platform). */
function adsSection(flow: Flow, brand: string): Transcript | null {
  const { session, snapshot, data, items } = flow;
  const platforms = namedSupportedPlatforms(session.answers);
  items.push(agentLine('quiz-done', [COPY.quizDone, ...(platforms.includes('meta') ? [metaAdsLine(brand)] : [])]));

  for (const platform of platforms) {
    const position = adsStepPosition(session, snapshot, data.adAccounts, platform);
    // A platform the BFF cannot discover for has no card and no step.
    if (position.kind === 'unsupported') continue;
    // The card is the job's own state (Queued, Searching, Found N): it shows at once, not when the accounts arrive.
    items.push(agentLine(`ads:${platform}`, [], { kind: 'ads', platform }, 'scout'));

    const label = platformLabel(platform);
    switch (position.kind) {
      case 'waiting':
        return stopAt(flow, 'ads', null, true);
      case 'card-only':
        break;
      case 'no-accounts':
        items.push(agentLine(`ads-none:${platform}`, adsNone(label), undefined, 'scout'));
        break;
      case 'confirmed': {
        const { confirmation, accounts } = position;
        items.push(
          userBubble(
            `ads-confirmed:${platform}`,
            confirmation.skipped || confirmation.accountIds.length === 0
              ? COPY.skipped
              : `${label}: ${accountNames(platform, confirmation.accountIds, accounts)}`,
          ),
        );
        break;
      }
      case 'docked':
        return stopAt(
          flow,
          'ads',
          {
            kind: 'adAccounts',
            platform,
            platformLabel: label,
            title: adAccountsTitle(label),
            subtitle: ADS_ACCOUNTS_SUBTITLE,
            accounts: position.entry.accounts,
            searching: position.searching,
          },
          true,
        );
    }
  }
  return null;
}

/**
 * 4b. Scout's funnel card (phase 4, step 3.1), after the ads. It is the funnel job's own state, drawn only when the BFF
 * started one (a snapshot with no funnel job has none to show). It asks nothing, so it never stops the flow.
 */
function funnelSection(flow: Flow): Transcript | null {
  const { snapshot, items } = flow;
  if (jobState(snapshot, 'funnel') === 'absent' || jobState(snapshot, 'funnel') === 'none') return null;
  items.push(agentLine('funnel', [], { kind: 'funnel' }, 'scout'));
  return null;
}

/** 5-6. J3 the brand guide, inline, then J4 the brand profile to confirm (J8: nothing after it is drawn until it is). */
function profileSection(flow: Flow, brand: string): Transcript | null {
  const { session, items } = flow;
  items.push(agentLine('brand-guide', [], { kind: 'brandGuide' }));
  // A session that was built before this step existed has no profile and is not asked for one afterwards.
  const built = session.completedAt !== null;
  if (session.brandProfile === null && !built) {
    items.push(agentLine('brand-profile-intro', [COPY.brandProfileIntro]));
    return stopAt(flow, 'brandProfile', { kind: 'brandProfile', brand }, true);
  }
  if (session.brandProfile !== null) items.push(agentLine('brand-profile-intro', [COPY.brandProfileIntro]));
  return null;
}

/** 7. J5 the competitors research, then the pick (J6: the cohort line once it is confirmed). */
function competitorsSection(flow: Flow, brand: string): Transcript | null {
  const { session, snapshot, data, items } = flow;
  const built = session.completedAt !== null;
  items.push(agentLine('competitors-research', [], { kind: 'competitorsResearch', brand }, 'scout'));
  const comp = data.competitors;
  if (!comp || comp.state === 'idle' || comp.state === 'loading') return stopAt(flow, 'competitors', null, true);
  if (comp.state === 'error') {
    items.push(agentLine('competitors-failed', [...COPY.competitorsFailed], undefined, 'scout'));
    return null;
  }

  const confirmation = snapshot?.confirmations.competitors ?? comp.data.confirmation;
  const list = comp.data.competitors;
  const searching = !isTerminalState(comp.data.status);
  if (confirmation) {
    const names = new Map(list.map((c) => [c.brandId, c.name]));
    const none = confirmation.skipped || confirmation.brandIds.length === 0;
    items.push(
      userBubble('competitors-confirmed', none ? COPY.skipped : confirmation.brandIds.map((id) => names.get(id) ?? id).join(', ')),
    );
    /* J6: the cohort line, in place of TP's "opened it on the right" (there is no side panel). */
    items.push(
      agentLine(
        'cohort-ready',
        none ? [...COPY.cohortSkipped] : [cohortReadyLine(confirmation.brandIds.length, brand)],
        undefined,
        'scout',
      ),
    );
    return null;
  }
  // Closed by Build without an answer (the read had failed): leave it out.
  if (built) return null;
  if (list.length === 0 && searching) return stopAt(flow, 'competitors', null, true);
  if (list.length === 0) {
    items.push(agentLine('competitors-none', [...COPY.competitorsNone], undefined, 'scout'));
    return null;
  }
  return stopAt(
    flow,
    'competitors',
    { kind: 'competitors', title: COPY.competitorsTitle, subtitle: competitorsSubtitle(COMPETITOR_PICKS_MAX, list), competitors: list, max: COMPETITOR_PICKS_MAX },
    true,
  );
}

/** 8. J7: the hand-off, with the Build card inline until it is pressed. */
function handoffSection(flow: Flow, brand: string): Transcript {
  const { session, snapshot, items } = flow;
  if (session.completedAt === null) {
    items.push(agentLine('handoff', [...COPY.handoff], { kind: 'build', brand, ready: snapshot?.quizSubmitted === true }));
    return stopAt(flow, 'build', null, true);
  }
  items.push(agentLine('handoff', [...COPY.handoff]));
  items.push(userBubble('build', COPY.finishBubble));
  return stopAt(flow, 'complete', null, true);
}

export function buildTranscript(
  session: OnboardingSessionState,
  snapshot: SessionSnapshot | null,
  data: TranscriptData = {},
): Transcript {
  const flow: Flow = {
    session,
    snapshot,
    data,
    items: [userBubble('opener', COPY.opener), agentLine('ask-site', [...COPY.askSite])],
  };
  const brand = brandLabel(snapshot, session.url, data.brandGuideName);
  // Each section either stops the chat where it is (a dock, or waiting on data) or hands over to the next one.
  return (
    urlSection(flow) ??
    quizSection(flow) ??
    adsSection(flow, brand) ??
    funnelSection(flow) ??
    profileSection(flow, brand) ??
    competitorsSection(flow, brand) ??
    handoffSection(flow, brand)
  );
}

/* ----------------------- derived views of the snapshot ------------------- */

/** `absent`: the job behind the part was never started by the BFF, so there is nothing to wait for or to fail at. */
export type PartState = 'loading' | 'value' | 'unavailable' | 'absent';

export interface ResearchRow {
  name: { state: PartState; value: string | null };
  category: { state: PartState; value: string | null };
  products: { state: PartState; value: number | null };
  liveAds: { state: PartState; value: number | null };
  /** The check mark: only when the research job itself is done. */
  checked: boolean;
}

function jobState(snapshot: SessionSnapshot | null, kind: string): StepState | 'absent' | 'none' {
  if (!snapshot) return 'none';
  return snapshot.jobs.find((j) => j.kind === kind)?.state ?? 'absent';
}

function part<T>(value: T | null, state: ReturnType<typeof jobState>): { state: PartState; value: T | null } {
  if (value !== null) return { state: 'value', value };
  if (state === 'none' || state === 'queued' || state === 'running') return { state: 'loading', value: null };
  if (state === 'absent') return { state: 'absent', value: null };
  return { state: 'unavailable', value: null };
}

/** The single-row research card: each part is a spinner, a value or "couldn't read", on its own job. */
export function researchRow(snapshot: SessionSnapshot | null, brandGuide: BrandGuideResponse | null = null): ResearchRow {
  const summary = snapshot?.research;
  return {
    name: part(summary?.name ?? null, jobState(snapshot, 'siteProfile')),
    category: part(summary?.category ?? null, jobState(snapshot, 'research')),
    // The site read counts Shopify products only; a store without that list still has CI's top sellers (both sources, merged).
    products: part(researchProductCount(summary, brandGuide), jobState(snapshot, 'siteProfile')),
    liveAds: part(summary?.liveAdCount ?? null, jobState(snapshot, 'adDiscovery:meta')),
    checked: jobState(snapshot, 'research') === 'done',
  };
}

/** True while the research (CI) job is queued or running: the fields only it can fill are not missing yet, just pending. */
export function researchPending(snapshot: SessionSnapshot | null): boolean {
  const state = jobState(snapshot, 'research');
  return state === 'queued' || state === 'running';
}

/** True while any job the BFF started is still queued or running. */
export function hasActiveJobs(snapshot: SessionSnapshot | null): boolean {
  if (!snapshot) return true;
  return snapshot.jobs.some((j) => !isTerminalState(j.state));
}

/** Names of the ad-discovery jobs in a snapshot, as platforms. */
export function snapshotAdPlatforms(snapshot: SessionSnapshot): AdPlatform[] {
  return snapshot.jobs.flatMap((j) => {
    const platform = adPlatformOfJobKind(j.kind);
    return platform ? [platform] : [];
  });
}

